import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import { MainLayout } from './components/layout/MainLayout';
import { AuthLayout } from './components/layout/AuthLayout';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { ROUTES } from './config/routes';

// Auth Pages
import { Login } from './pages/auth/Login';
import { Register } from './pages/auth/Register';
import { ForgotPassword } from './pages/auth/ForgotPassword';

// Dashboard
import { Dashboard } from './pages/dashboard/Dashboard';

// Proposal Pages
import { ProposalList } from './pages/proposal/ProposalList';
import { ProposalDetail } from './pages/proposal/ProposalDetail';
import { CustomerList } from './pages/proposal/CustomerList';
import { CustomerDetail } from './pages/proposal/CustomerDetail';

// Scout Pages
import { InfluencerSearch } from './pages/scout/InfluencerSearch';
import { InfluencerDetail } from './pages/scout/InfluencerDetail';
import { ScreeningTasks } from './pages/scout/ScreeningTasks';

// Outreach Pages
import { CampaignList } from './pages/outreach/CampaignList';
import { CampaignDetail } from './pages/outreach/CampaignDetail';
import { Replies } from './pages/outreach/Replies';
import { ReplyDetail } from './pages/outreach/ReplyDetail';
import { EmailAccounts } from './pages/outreach/EmailAccounts';

// Knowledge Pages
import { KnowledgeBase } from './pages/knowledge/KnowledgeBase';

// Notification Pages
import { NotificationList } from './pages/notification/NotificationList';

// Settings Pages
import { Settings } from './pages/settings/Settings';
import { Profile } from './pages/settings/Profile';
import { TenantSettings } from './pages/settings/TenantSettings';
import { MemberManagement } from './pages/settings/MemberManagement';
import { RoleManagement } from './pages/settings/RoleManagement';
import { Subscription } from './pages/settings/Subscription';
import { Usage } from './pages/settings/Usage';
import { BillingManagement } from './pages/settings/BillingManagement';
import { PaymentPage } from './pages/settings/PaymentPage';
import { DataExport } from './pages/settings/DataExport';
import { AccountLifecycle } from './pages/settings/AccountLifecycle';

// Admin Pages
import { AdminDashboard } from './pages/admin/AdminDashboard';
import { TenantManagement } from './pages/admin/TenantManagement';
import { TenantDetail } from './pages/admin/TenantDetail';
import { AnnouncementManagement } from './pages/admin/AnnouncementManagement';

// Not Found
import { NotFound } from './pages/NotFound';

const App: React.FC = () => {
  return (
    <ConfigProvider locale={zhCN}>
      <BrowserRouter>
        <Routes>
          {/* Auth Routes */}
          <Route element={<AuthLayout />}>
            <Route path={ROUTES.LOGIN} element={<Login />} />
            <Route path={ROUTES.REGISTER} element={<Register />} />
            <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPassword />} />
          </Route>

          {/* Protected Routes */}
          <Route element={<ProtectedRoute />}>
            <Route element={<MainLayout />}>
              {/* Dashboard */}
              <Route path="/" element={<Navigate to={ROUTES.DASHBOARD} replace />} />
              <Route path={ROUTES.DASHBOARD} element={<Dashboard />} />

              {/* Proposal Routes */}
              <Route path={ROUTES.PROPOSALS} element={<ProposalList />} />
              <Route path={ROUTES.PROPOSAL_DETAIL} element={<ProposalDetail />} />

              {/* Customer Routes */}
              <Route path={ROUTES.CUSTOMERS} element={<CustomerList />} />
              <Route path={ROUTES.CUSTOMER_DETAIL} element={<CustomerDetail />} />

              {/* Scout Routes */}
              <Route path={ROUTES.SCOUT_SEARCH} element={<InfluencerSearch />} />
              <Route path={ROUTES.SCOUT_INFLUENCER} element={<InfluencerDetail />} />
              <Route path={ROUTES.SCOUT_SCREENING} element={<ScreeningTasks />} />

              {/* Outreach Routes */}
              <Route path={ROUTES.OUTREACH_CAMPAIGNS} element={<CampaignList />} />
              <Route path={ROUTES.OUTREACH_CAMPAIGN_DETAIL} element={<CampaignDetail />} />
              <Route path={ROUTES.OUTREACH_REPLIES} element={<Replies />} />
              <Route path={ROUTES.OUTREACH_REPLY_DETAIL} element={<ReplyDetail />} />
              <Route path={ROUTES.OUTREACH_EMAIL_ACCOUNTS} element={<EmailAccounts />} />

              {/* Knowledge Routes */}
              <Route path={ROUTES.KNOWLEDGE} element={<KnowledgeBase />} />

              {/* Notification Routes */}
              <Route path={ROUTES.NOTIFICATIONS} element={<NotificationList />} />

              {/* Profile Route */}
              <Route path={ROUTES.PROFILE} element={<Profile />} />

              {/* Settings Routes */}
              <Route path={ROUTES.SETTINGS} element={<Settings />} />
              <Route path={ROUTES.SETTINGS_TENANT} element={<TenantSettings />} />
              <Route path={ROUTES.SETTINGS_MEMBERS} element={<MemberManagement />} />
              <Route path={ROUTES.SETTINGS_ROLES} element={<RoleManagement />} />
              <Route path={ROUTES.SETTINGS_SUBSCRIPTION} element={<Subscription />} />
              <Route path={ROUTES.SETTINGS_USAGE} element={<Usage />} />
              <Route path={ROUTES.SETTINGS_BILLING} element={<BillingManagement />} />
              <Route path={ROUTES.SETTINGS_PAYMENT} element={<PaymentPage />} />
              <Route path={ROUTES.SETTINGS_DATA_EXPORT} element={<DataExport />} />
              <Route path={ROUTES.SETTINGS_ACCOUNT_LIFECYCLE} element={<AccountLifecycle />} />

              {/* Admin Routes */}
              <Route path={ROUTES.ADMIN} element={<AdminDashboard />} />
              <Route path={ROUTES.ADMIN_TENANTS} element={<TenantManagement />} />
              <Route path={ROUTES.ADMIN_TENANT_DETAIL} element={<TenantDetail />} />
              <Route path={ROUTES.ADMIN_ANNOUNCEMENTS} element={<AnnouncementManagement />} />
            </Route>
          </Route>

          {/* 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </ConfigProvider>
  );
};

export default App;
